import { createSelector } from 'reselect';

import type { RootState } from 'config/store/reducers';

import { TABS } from 'constants/Layout';
import { getI18nMessage } from 'utils/utils';
import { CHECKLIST_GROUP_STATUS } from 'modules/entity/subEntities/intervention/constants';
import { getPayments } from 'modules/entity/subEntities/intervention/selectors/payment';
import { getDefaultLanguage, getEntity, getLanguage, getPayers } from 'modules/entity/selectors';

import { getUI, getSelectedChecklistGroupId } from '.';
import { getAdvice } from './advices';

export const getRoot = (state: RootState) => state;

const getProps = <T, >(_: RootState, props: T) => props;

const getTabs = createSelector(getUI, (ui) => ui.tabs);
export const getSelectedTab = createSelector(getTabs, (tabs) => tabs.selected);
export const isPaymentTab = createSelector(getSelectedTab, (tab) => tab === TABS.PAYMENT_TAB);
export const showVAT = createSelector(getUI, (ui) => ui.vatChecked);

const getChecklistGroupById = createSelector(getEntity, getProps, ({ checklistGroups = [] }, id) =>
  checklistGroups.find(({ id: groupId }) => groupId === id));
const getSelectedChecklistGroup = createSelector(getRoot, getSelectedChecklistGroupId, getChecklistGroupById);
const isSelectedChecklistGroupAccepted = createSelector(
  getSelectedChecklistGroup,
  (group) => !group || group?.status === CHECKLIST_GROUP_STATUS.ACCEPTED
);

export const canAnswer = createSelector(
  getEntity,
  getSelectedChecklistGroup,
  ({ answerUrl, payOnlineUrl, payDepositUrl }, checklistGroup) =>
    Boolean(answerUrl || payOnlineUrl || payDepositUrl) &&
    (!checklistGroup || checklistGroup?.status !== CHECKLIST_GROUP_STATUS.REFUSED)
);

export const shouldShowPaymentHistory = createSelector(
  getPayments,
  isSelectedChecklistGroupAccepted,
  (payments, isAccepted) => payments.length > 0 && isAccepted,
);

export const hasPaymentUrls = createSelector(getEntity, ({ payOnlineUrl, payDepositUrl }) =>
  Boolean(payOnlineUrl || payDepositUrl));

export const getPayerByAdviceId = createSelector(getPayers, getAdvice, (payers, advice) => {
  const { payerId } = advice;
  return payers.find(({ id }) => id === payerId);
});

const getPayerRawName = createSelector(getPayerByAdviceId, payer => payer?.name);
export const getPayerNameByAdviceId = createSelector(
  getLanguage,
  getDefaultLanguage,
  getPayerRawName,
  getI18nMessage,
);
